import type { AuthedFetchOptions, RefreshOutcome } from "./types";

/**
 * Détecte une réponse « jeton d'accès invalide » — y compris quand tRPC la
 * cache derrière un 207.
 *
 * `httpBatchLink` regroupe plusieurs procédures dans une seule requête HTTP, et
 * `getHTTPStatusCode` (@trpc/server) ne renvoie un statut unique que si TOUTES
 * les entrées du lot partagent le même : dès qu'un lot mêle une procédure
 * publique (200) et une procédure protégée (401), il sort en **207
 * Multi-Status**. Un intercepteur qui ne teste que `status === 401` ne voit donc
 * jamais rien : pas de refresh, pas de redirection, et les requêtes protégées
 * échouent en silence (skeletons qui tournent, listes vides).
 *
 * Un corps 200 ne peut pas contenir d'entrée 401 — ce mélange produirait un 207
 * — donc inutile de cloner et parser les réponses réussies.
 */
export async function isUnauthorizedResponse(response: Response): Promise<boolean> {
  if (response.status === 401) return true;
  if (response.status !== 207) return false;
  try {
    const body = await response.clone().json();
    const items = Array.isArray(body) ? body : [body];
    return items.some(
      (item: { error?: { data?: { code?: string } } }) =>
        item?.error?.data?.code === "UNAUTHORIZED"
    );
  } catch {
    return false;
  }
}

function withFreshHeaders(
  init: RequestInit | undefined,
  buildHeaders?: () => Record<string, string>
): RequestInit {
  const next: RequestInit = { ...init, credentials: "include" };
  if (!buildHeaders) return next;
  // `init.headers` peut être un objet, un tableau ou une instance Headers selon
  // l'appelant — `new Headers()` normalise les trois.
  const headers = new Headers(init?.headers as HeadersInit | undefined);
  for (const [key, value] of Object.entries(buildHeaders())) headers.set(key, value);
  next.headers = headers;
  return next;
}

/**
 * `fetch` qui absorbe l'expiration du jeton d'accès : sur 401 (ou 207 masquant
 * un 401), il rafraîchit la session puis rejoue la requête UNE fois.
 */
export function createAuthedFetch(
  tryRefreshSession: () => Promise<RefreshOutcome>,
  options: AuthedFetchOptions
): typeof fetch {
  const { buildHeaders, onSessionLost, onTransientError } = options;

  return async function authedFetch(input: RequestInfo | URL, init?: RequestInit) {
    const response = await fetch(input, { ...init, credentials: "include" });

    if (!(await isUnauthorizedResponse(response))) return response;

    const outcome = await tryRefreshSession();

    if (outcome === "unavailable") {
      // Serveur injoignable / 5xx / 429 : on ne SAIT PAS si la session est
      // morte. La déconnecter ici, c'est éjecter un utilisateur simplement
      // hors-ligne. On rend le 401 tel quel : l'app l'affichera comme une
      // erreur de requête, récupérable au prochain essai.
      onTransientError?.();
      return response;
    }

    if (outcome === "unauthenticated") {
      onSessionLost();
      return response;
    }

    // Rejeu unique, avec des en-têtes RECONSTRUITS : le serveur régénère un
    // jeton CSRF à chaque refresh, donc rejouer avec ceux d'origine ferait
    // échouer toute mutation en « 403 Invalid CSRF token ».
    const replay = await fetch(input, withFreshHeaders(init, buildHeaders));
    if (await isUnauthorizedResponse(replay)) {
      // Toujours refusé après un refresh réussi : la session est bien finie.
      // Sans cette réinspection, l'app restait coincée dans un état ni
      // connecté ni redirigé.
      onSessionLost();
    }
    return replay;
  };
}
